import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { label } from "../../lib/format";
import { Card, Field, PageHeader, Td, Th } from "../../components/common/UI";
import { StatusBadge } from "../../components/common/Badges";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const DOC_TYPES = ["draft_measure", "committee_report", "executive_action_proof", "review_transmittal", "review_findings", "proof_of_publication", "certificate_of_posting", "legal_memorandum", "minutes_excerpt", "supporting_reference", "other"];
const FLAGS = [["requires_reason", "Requires reason"], ["enforce_separation", "Separation of duties"], ["assigns_number", "Assigns official number"], ["records_approval", "Records approval"], ["is_active", "Active"]];

const Multi = ({ options, value, onChange, testId }) => (
  <select multiple data-testid={testId} className="otms-input h-28" value={value} onChange={(e) => onChange([...e.target.selectedOptions].map((o) => o.value))}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

function TransitionDialog({ wf, t, onClose, onSaved }) {
  const [f, setF] = useState({ required_fields: [], required_document_types: [], task_template: null, is_active: true, ...t, task_title: t.task_template?.title || "", task_role: t.task_template?.role_code || "", task_days: t.task_template?.due_days || "" });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    const body = {
      from_stage_id: f.from_stage_id, to_stage_id: f.to_stage_id, name: f.name, required_permission: f.required_permission, required_fields: f.required_fields,
      required_document_types: f.required_document_types, ...Object.fromEntries(FLAGS.map(([k]) => [k, !!f[k]])),
      task_template: f.task_title ? { title: f.task_title, role_code: f.task_role || null, due_days: f.task_days ? Number(f.task_days) : null } : null,
    };
    try {
      t.id ? await api.put(`/workflow-transitions/${t.id}`, body) : await api.post(`/workflows/${wf.id}/transitions`, body);
      toast.success("Transition saved");
      onClose();
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const stageOpts = wf.stages.map((s) => <option key={s.id} value={s.id}>{s.sequence}. {s.name}</option>);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="transition-config-dialog">
        <DialogHeader><DialogTitle>{t.id ? "Edit transition" : "New transition"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="From stage"><select className="otms-input" value={f.from_stage_id || ""} onChange={set("from_stage_id")} data-testid="tc-from"><option value="">—</option>{stageOpts}</select></Field>
          <Field label="To stage"><select className="otms-input" value={f.to_stage_id || ""} onChange={set("to_stage_id")} data-testid="tc-to"><option value="">—</option>{stageOpts}</select></Field>
          <Field label="Action name"><input className="otms-input" value={f.name || ""} onChange={set("name")} data-testid="tc-name" /></Field>
          <Field label="Required permission"><select className="otms-input" value={f.required_permission || ""} onChange={set("required_permission")} data-testid="tc-perm"><option value="">—</option>{wf.permission_options.map((p) => <option key={p}>{p}</option>)}</select></Field>
          <Field label="Required fields"><Multi testId="tc-fields" options={Object.entries(wf.field_options)} value={f.required_fields} onChange={set("required_fields")} /></Field>
          <Field label="Required documents"><Multi testId="tc-docs" options={DOC_TYPES.map((d) => [d, label(d)])} value={f.required_document_types} onChange={set("required_document_types")} /></Field>
          <div className="space-y-1.5 md:col-span-2">
            {FLAGS.map(([k, l]) => <label key={k} className="mr-5 inline-flex items-center gap-2 text-xs"><input type="checkbox" checked={!!f[k]} onChange={(e) => set(k)(e.target.checked)} data-testid={`tc-${k}`} />{l}</label>)}
          </div>
          <Field label="Task title on entry" hint="Use {ref} for the measure reference"><input className="otms-input" value={f.task_title} onChange={set("task_title")} /></Field>
          <Field label="Task role code / fallback due days"><div className="flex gap-2"><input className="otms-input" placeholder="role code" value={f.task_role} onChange={set("task_role")} /><input className="otms-input w-24" placeholder="days" value={f.task_days} onChange={set("task_days")} /></div></Field>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-sky-600 hover:bg-sky-700" onClick={save} data-testid="tc-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function WorkflowConfig() {
  const [defs, setDefs] = useState([]);
  const [wf, setWf] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback((id) => api.get(`/workflows/${id}`).then((r) => setWf(r.data)), []);
  useEffect(() => {
    api.get("/workflows").then((r) => { setDefs(r.data); if (r.data[0]) load(r.data[0].id); });
  }, [load]);
  return (
    <div className="fade-up space-y-6">
      <PageHeader eyebrow="Administration" title="Workflow Configuration" subtitle="Stages and permitted transitions are data, not code. Every transition is validated server-side against these rules."
        actions={defs.length > 1 && <select className="otms-input w-64" onChange={(e) => load(e.target.value)}>{defs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select>} />
      {wf && (
        <>
          <Card title={`${wf.name} · v${wf.version}`} testId="workflow-stages">
            <p className="mb-3 text-xs text-slate-500">{wf.description}</p>
            <div className="flex flex-wrap gap-2">
              {wf.stages.map((s) => (
                <div key={s.id} className="rounded border border-slate-200 px-2.5 py-1.5 text-xs" data-testid={`wf-stage-${s.code}`}>
                  <span className="mr-1 text-slate-400">{s.sequence}.</span><StatusBadge category={s.category} name={s.name} />
                  {s.locks_record && <span className="ml-1 text-[10px] text-slate-500">locks</span>}
                </div>
              ))}
            </div>
          </Card>
          <Card title={`Transitions (${wf.transitions.length})`} testId="workflow-transitions"
            actions={<Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={() => setEdit({})} data-testid="add-transition-button"><Plus className="mr-1 h-4 w-4" />Add</Button>}>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead><tr><Th>Action</Th><Th>From → To</Th><Th>Permission</Th><Th>Requirements</Th><Th>Flags</Th><Th /></tr></thead>
                <tbody>
                  {wf.transitions.map((t) => (
                    <tr key={t.id} className={t.is_active ? "" : "opacity-50"}>
                      <Td className="font-medium">{t.name}</Td>
                      <Td>{t.from_stage_name} → {t.to_stage_name}</Td>
                      <Td><code className="text-[11px]">{t.required_permission}</code></Td>
                      <Td>{[...(t.required_fields || []).map((x) => wf.field_options[x] || x), ...(t.required_document_types || []).map((d) => `doc: ${label(d)}`)].join(", ") || "—"}</Td>
                      <Td className="text-[11px]">{FLAGS.filter(([k]) => k !== "is_active" && t[k]).map(([, l]) => l).join(", ") || "—"}</Td>
                      <Td><Button size="sm" variant="ghost" onClick={() => setEdit(t)} data-testid={`edit-transition-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
      {edit && wf && <TransitionDialog wf={wf} t={edit} onClose={() => setEdit(null)} onSaved={() => load(wf.id)} />}
    </div>
  );
}
